/// <reference types="google.maps" />

import { MaskedElement } from '@afterpaytouch/integrations'
import { importLibrary, setOptions } from '@googlemaps/js-api-loader'
import { clsx } from 'clsx'
import { parse, toNormalised } from 'postcode'
import React, { FunctionComponent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import usePlacesAutocomplete, { getGeocode, Suggestion } from 'use-places-autocomplete'

import { Button } from '../Button'
import { Dropdown, DropdownMenuBorderStyle } from '../Dropdown'
import { Grid } from '../Grid'
import { Icon } from '../Icon'
import { Input, InputOnChange, InputOnFocus } from '../Input'
import styles from '../InputAddress/style.module.scss'
import { DesignLanguage, useIsDesignLanguage, useThemeClass } from '../Theme'
import { Text } from '../Typography'
import { KeyCodes } from '../utils'
import { warn } from '../utils/console'
import { noop } from '../utils/function'
import {
  COUNTRIES_WITHOUT_POSTCODES,
  DEFAULT_ALLOWED_ADDRESS_TYPES,
  DEFAULT_DEBOUNCE_VALUE,
  DEFAULT_OPTIONAL_ADDRESS_FIELDS,
  INPUT_ADDRESS_DROPDOWN_MENU,
  STATES,
} from './constants'
import { CustomGeocodeResult, doGeocodeV2, getErrorMessage, parseAddress, POSTCODE_FORMAT, postcodeKind, scrollDropDownMenu } from './helper'
import { InputAddressData, InputAddressDecodingStatus, InputAddressProps, InputAddressValidationRule, MandatoryFields } from './types'

export const InputAddress: FunctionComponent<InputAddressProps> = ({
  name = '',
  address1Label = '',
  address2Label = '',
  allowedAddressType = DEFAULT_ALLOWED_ADDRESS_TYPES,
  key: keyProp,
  apiKey,
  autoFocus = true,
  showAutoCompeleteErrorTextOnly = true,
  componentRestrictions = {
    country: 'US',
  },
  debounce = DEFAULT_DEBOUNCE_VALUE,
  disabled = false,
  error = false,
  errorText = '',
  onManualErrorTrigger = () => {},
  focusOnSelect = true,
  initialAddress,
  isRegions = false,
  label = '',
  language,
  libraries = ['places'],
  manualAddressLabel = '',
  manualAddressLabelCaption = '',
  minSearchLength = 3,
  onAddressChange = noop,
  onGeolocation = noop,
  onAddressBlur = noop,
  onInputValueChange = noop,
  onInputValueBlur = noop,
  onManualModeChange = noop,
  onManualAddressChange = noop,
  onCustomValidation = noop,
  onGeocodeResult = noop,
  onManualAddressLabelClick = noop,
  placesAutocompleteType = ['address'],
  postcodeLabel = '',
  readOnly = false,
  region,
  searchAddressLabel = '',
  size = 'Large',
  stateLabel = '',
  suburbLabel = '',
  testNameSpace = '',
  validateRule = InputAddressValidationRule.ByAddressType,
  v,
  version,
  useLocalityLongName = false,
  showManualAddressLabelInMenu = false,
  icon,
  iconPosition,
  showManualAddressFieldsOnSelect = false,
  address2InfoText = '',
  defaultExpanded = false,
  optionalManualAddressFields = DEFAULT_OPTIONAL_ADDRESS_FIELDS,
  isFullPageSuggestionsEnabled = false,
}) => {
  const [sdkLoaded, setSdkLoaded] = useState(false)
  const { init, suggestions, setValue, clearSuggestions } = usePlacesAutocomplete({
    debounce,
    initOnMount: false, // Disable initializing when the component mounts, default is true
    requestOptions: {
      types: placesAutocompleteType,
      componentRestrictions,
    },
  })

  const [address, setAddress] = useState({
    query: '',
    status: InputAddressDecodingStatus.Pristine,
    key: Math.random().toString(36),
    valid: !error,
  })
  const [manualInput, setManualInput] = useState(defaultExpanded)
  const address1Ref = useRef<HTMLInputElement>(null)
  const address2Ref = useRef<HTMLInputElement>(null)
  const autoCompleteRef = useRef<HTMLInputElement>(null)
  const autoCompleteParentRef = useRef<HTMLDivElement>(null)
  const toggleCountRef = useRef<number>(0)
  const addressChangeCountRef = useRef<number>(0)
  const postcodeFormatRef = useRef<number>(0)
  const componentRef = useRef<HTMLDivElement>(null)
  const country = (Array.isArray(componentRestrictions?.country) ? componentRestrictions.country[0] : componentRestrictions?.country) ?? 'US'
  const statesData = STATES[country]
  const themeClass = useThemeClass()

  const getInitialAddress = useCallback((): InputAddressData => {
    if (typeof initialAddress !== 'undefined' && defaultExpanded) {
      return { ...(Array.isArray(initialAddress) ? initialAddress[0] : initialAddress), countryCode: country }
    }
    return {
      address1: '',
      address2: '',
      area2: '',
      countryCode: country,
      postcode: '',
      state: '',
      suburb: '',
    }
  }, [defaultExpanded, initialAddress, country])
  const emptyAddress = {
    address1: '',
    address2: '',
    area2: '',
    countryCode: country,
    postcode: '',
    state: '',
    suburb: '',
  }
  const initialState = getInitialAddress()
  const [formData, setFormData] = useState<InputAddressData>(initialState)
  const showError = address.status === InputAddressDecodingStatus.Pristine || address.status === InputAddressDecodingStatus.Resolved
  const isError =
    (showError && error) ||
    (!address.valid && (address.status === InputAddressDecodingStatus.NotStarted || address.status === InputAddressDecodingStatus.Resolved))
  const [customValidationString, setCustomValidationString] = useState<string>('')
  const listOfOptionalManualAddressFields = optionalManualAddressFields.concat(DEFAULT_OPTIONAL_ADDRESS_FIELDS)
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [showFullPageAddress, setShowFullPageAddress] = useState(false)
  const [focusIndex, setFocusIndex] = useState(0)
  const dropdownSuggestionRefs = React.useRef(new Array<HTMLElement>())
  const focusedAutocompleteSuggestionId = dropdownSuggestionRefs.current[focusIndex]?.id ?? ''
  const isShowManualAddressLabelInMenu = showManualAddressLabelInMenu && manualAddressLabel.length > 0
  const isArcadeDesign = useIsDesignLanguage(DesignLanguage.Arcade)
  const isShowingFullPageAddress = isFullPageSuggestionsEnabled && showFullPageAddress
  const iconPropsForAutocomplete: Pick<InputAddressProps, 'icon' | 'iconPosition'> = isArcadeDesign
    ? {
        icon: 'Search',
        iconPosition: 'Left',
      }
    : {
        icon,
        iconPosition,
      }

  const addressParser = parseAddress({
    country,
    useLocalityLongName,
    allowedAddressType,
    isRegions,
    validateRule,
    optionalFields: listOfOptionalManualAddressFields,
  })

  const handleInput = (e: InputOnChange): void => {
    const query = e.target.value
    const queryLength = query?.trim().length
    const isQueryValid = queryLength > 0
    const shouldPingApi = queryLength >= minSearchLength

    setValue(e.target.value, shouldPingApi)

    setAddress({
      ...address,
      query,
      status: InputAddressDecodingStatus.NotStarted,
      valid: isQueryValid,
    })
    onInputValueChange(query)
    setShowFullPageAddress(query.length > 0)
    if (!isQueryValid) {
      setFormData(emptyAddress)
      onAddressChange(emptyAddress, true)
    }
  }

  const handleSelect = ({ place_id: placeID }: Suggestion): void => {
    if (placeID === null) {
      return
    }

    setAddress({
      ...address,
      status: InputAddressDecodingStatus.Pending,
      valid: false,
    })
    setFocusIndex(0)

    getGeocode({ placeId: placeID })
      .then((results) => {
        const firstMatchedPlace = results[0]
        const { valid, data } = addressParser(firstMatchedPlace)
        const newFormData = valid ? data : emptyAddress
        onGeolocation({
          lat: firstMatchedPlace?.geometry?.location?.lat(),
          long: firstMatchedPlace?.geometry?.location?.lng(),
          placeID,
        })
        setAddress({
          ...address,
          key: Math.random().toString(36),
          query: firstMatchedPlace.formatted_address,
          status: InputAddressDecodingStatus.Resolved,
          valid,
        })

        setFormData(newFormData)
        onAddressChange(newFormData, true, valid)
        onAddressBlur(valid)
        clearSuggestions()
        if (showManualAddressFieldsOnSelect) {
          setManualInput(true)
        } else if (focusOnSelect) {
          autoCompleteRef.current?.focus()
        }
      })
      .catch(() => {
        clearSuggestions()
        resetAddressForm()
        onManualErrorTrigger()
      })
  }

  const renderOptionString = (suggestion: Suggestion): ReactNode => {
    const { description, matched_substrings: matchedSubstrings, place_id: placeId } = suggestion

    if (matchedSubstrings.length === 0) {
      return description
    }

    const beforeHighlight: ReactNode[] = [description.slice(0, matchedSubstrings[0].offset)]
    const hightlightedText = matchedSubstrings.map((matchedSubstring: google.maps.places.PredictionSubstring, index): ReactNode[] => {
      const { offset, length } = matchedSubstring
      const nextOffset = matchedSubstrings[index + 1]?.offset
      const highlightTextEnd = offset + length
      const highlightText = description.slice(offset, highlightTextEnd)
      const textAfterHighlighted = description.slice(highlightTextEnd, nextOffset ?? description.length)
      return [<strong key={`${placeId}-highlighted-${index}`}>{highlightText}</strong>, textAfterHighlighted]
    })
    const transformedAddress = beforeHighlight.concat(hightlightedText)
    // Important: Do not use ReactFragments for suggestions as Google Translate will break it:
    // https://github.com/facebook/react/issues/11538
    return transformedAddress.map((text, i) => <span key={`${placeId}-text-${i}`}>{text}</span>)
  }

  const handleManualAddressLabel = (): void => {
    onManualAddressLabelClick()
    handleToggleManualMode()
  }

  const handleToggleManualMode = (): void => {
    toggleCountRef.current++
    onManualModeChange(!manualInput)
    setShowFullPageAddress(false)
    setManualInput(!manualInput)
    if (defaultExpanded && toggleCountRef.current === 1) {
      // only set once
      const { address1, address2, area2, suburb, state, postcode } = initialState
      setAddress({
        query: `${[address1, address2, area2].filter(Boolean).join('/')}, ${[suburb, state, postcode].filter(Boolean).join(' ')}`,
        status: InputAddressDecodingStatus.Pristine,
        key: Math.random().toString(36),
        valid: true,
      })
    }
  }

  const getManualFieldError = (field: string): boolean => {
    return !listOfOptionalManualAddressFields.includes(field) && isError
  }

  const handlePostCodeFormat = (): void => {
    let isAddressValid = address.valid
    if (componentRestrictions?.country === 'GB') {
      const normalisedPostCode = toNormalised(formData.postcode)
      isAddressValid = typeof normalisedPostCode === 'string'
      setFormData({
        ...formData,
        postcode: normalisedPostCode ?? formData.postcode,
      })
      setAddress({
        ...address,
        status: InputAddressDecodingStatus.NotStarted,
        valid: isAddressValid,
      })
      postcodeFormatRef.current++
    }
    onAddressBlur(isAddressValid)
  }

  const handleManualAddressChange = (e: InputOnChange): void => {
    addressChangeCountRef.current++
    const newFormData = {
      ...formData,
      [e.target.name]: e.target.value,
    }

    setCustomValidationString(onCustomValidation(newFormData) ?? '')

    setFormData(newFormData)
    onAddressChange(newFormData, true)
    onManualAddressChange(newFormData)
  }

  const handleManualAddressBlur = (): void => {
    const mandatoryFields: MandatoryFields = {
      address1: formData.address1,
      suburb: formData.suburb,
      state: formData.state,
      postcode: formData.postcode,
      countryCode: formData.countryCode,
    }

    const mandatoryKeys = Object.keys(mandatoryFields).filter((item) => !optionalManualAddressFields.includes(item))
    const valid = mandatoryKeys.every((key) => mandatoryFields[key as keyof MandatoryFields]?.trim().length > 0)

    setAddress({
      ...address,
      valid,
    })
    onAddressBlur(valid)
  }

  const handleManualPostCodeChange = (e: InputOnChange): void => {
    addressChangeCountRef.current++

    const { postcode } = parse(e.target.value)
    const newFormData = {
      ...formData,
      [e.target.name]: postcode ?? e.target.value,
    }

    const mandatoryFields: MandatoryFields = {
      address1: newFormData.address1,
      suburb: newFormData.suburb,
      state: newFormData.state,
      postcode: newFormData.postcode,
      countryCode: newFormData.countryCode,
    }

    setCustomValidationString(onCustomValidation(newFormData) ?? '')
    const mandatoryKeys = Object.keys(mandatoryFields).filter((item) => !optionalManualAddressFields.includes(item))
    const valid = mandatoryKeys.every((key) => mandatoryFields[key as keyof MandatoryFields]?.trim().length > 0)

    setAddress({
      ...address,
      status: InputAddressDecodingStatus.Pristine,
      valid,
    })
    setFormData(newFormData)
    onAddressChange(newFormData, true, valid)
  }

  const handleStateSelect = (state: string): void => {
    addressChangeCountRef.current++
    const newFormData = {
      ...formData,
      state,
    }

    setCustomValidationString(onCustomValidation(newFormData) ?? '')

    setAddress({
      ...address,
      valid: true,
    })
    setFormData(newFormData)
    onAddressChange(newFormData, true)
    onAddressBlur(true)
  }

  const handleAutocompleteInputBlur = (e: InputOnFocus): void => {
    // Prevents blur from firing when clicking on an autocomplete suggestion.
    if (autoCompleteParentRef.current?.contains(e.relatedTarget) ?? false) {
      return
    }

    if (address.status === InputAddressDecodingStatus.NotStarted) {
      setAddress({
        ...address,
        status: InputAddressDecodingStatus.NotStarted,
        key: Math.random().toString(36),
        valid: false,
      })
      setFormData(emptyAddress)
      onAddressChange(emptyAddress, true)
      onAddressBlur(false)
      setShowFullPageAddress(false)
    }
    onInputValueBlur(e)
    clearSuggestions()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const suggestionListLength = suggestions.data.length
    // if show the manual address input, it will append an option in the suggestion list
    const lastIndex = isShowManualAddressLabelInMenu ? suggestionListLength : suggestionListLength - 1
    const hasSuggestion = suggestionListLength > 0
    const { key } = e
    if (key === KeyCodes.Enter) {
      e.preventDefault()
      if (hasSuggestion) {
        if (focusIndex >= suggestionListLength) {
          setManualInput(true)
        } else {
          handleSelect(suggestions.data[focusIndex])
        }
      }
    }

    if (key === KeyCodes.ArrowDown) {
      e.preventDefault()
      setFocusIndex(focusIndex >= lastIndex ? 0 : focusIndex + 1)
    }
    if (key === KeyCodes.ArrowUp) {
      e.preventDefault()
      setFocusIndex(focusIndex === 0 ? lastIndex : focusIndex - 1)
    }
  }

  const renderStateField = (): ReactNode => {
    if (typeof statesData === 'undefined' || statesData.order?.length <= 0) {
      return (
        <Input
          key={`${address.key}-state`}
          label={stateLabel}
          name='state'
          onChange={handleManualAddressChange}
          onBlur={handleManualAddressBlur}
          size={size}
          autoComplete='address-level1'
          value={formData.state}
          error={getManualFieldError('state')}
          disabled={disabled}
          readOnly={readOnly}
          {...(testNameSpace.length > 0 && { 'data-dd-action-name': `${testNameSpace} State` })}
          {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-state` })}
          fullWidth
        />
      )
    }

    return (
      <Dropdown
        defaultValue={formData.state}
        onSelect={handleStateSelect}
        label={stateLabel}
        autoComplete='address-level1'
        error={getManualFieldError('state')}
        disabled={disabled || readOnly}
        {...(testNameSpace.length > 0 && { 'data-dd-action-name': `${testNameSpace} State` })}
        {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-state` })}
      >
        {statesData.order?.map((state, index) => {
          return (
            <Dropdown.MenuItem key={`inputAddress-state-dropdown-${index}`} value={state}>
              {statesData.label[state]}
            </Dropdown.MenuItem>
          )
        })}
      </Dropdown>
    )
  }

  const renderPostcodeField = (): ReactNode | null => {
    if (!COUNTRIES_WITHOUT_POSTCODES.includes(country)) {
      return (
        <Grid.Item>
          <Input
            kind={postcodeKind(POSTCODE_FORMAT[country])}
            format={POSTCODE_FORMAT[country]}
            key={`${address.key}-${postcodeFormatRef.current}-postcode`}
            label={postcodeLabel}
            name='postcode'
            onChange={handleManualPostCodeChange}
            onBlur={handlePostCodeFormat}
            size={size}
            value={formData.postcode}
            autoComplete='postal-code'
            error={getManualFieldError('postcode')}
            disabled={disabled}
            readOnly={readOnly}
            {...(testNameSpace.length > 0 && { 'data-dd-action-name': `${testNameSpace} Postcode` })}
            {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-postcode` })}
          />
        </Grid.Item>
      )
    }
    return null
  }

  const renderManualInputs = (): ReactNode => {
    return (
      <>
        <div className={styles[themeClass]}>
          <Grid>
            <Grid.Item>
              <Input.Text
                key={`${address.key}-address1`}
                label={address1Label}
                name='address1'
                onChange={handleManualAddressChange}
                onBlur={handleManualAddressBlur}
                ref={address1Ref}
                size={size}
                autoComplete='address-line1'
                value={formData.address1}
                error={getManualFieldError('address1')}
                disabled={disabled}
                readOnly={readOnly}
                fullWidth
                {...(testNameSpace.length > 0 && { 'data-dd-action-name': `${testNameSpace} Address1` })}
                {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-address1` })}
              />
            </Grid.Item>
          </Grid>
          <Grid>
            <Grid.Item>
              <Input.Text
                key={`${address.key}-address2`}
                label={address2Label}
                name='address2'
                onChange={handleManualAddressChange}
                onBlur={handleManualAddressBlur}
                ref={address2Ref}
                autoComplete='address-line2'
                size={size}
                value={formData.address2}
                error={getManualFieldError('address2')}
                disabled={disabled}
                readOnly={readOnly}
                fullWidth
                {...(testNameSpace.length > 0 && { 'data-dd-action-name': `${testNameSpace} Address2` })}
                {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-address2` })}
                {...(address2InfoText.length > 0 && { infoText: address2InfoText })}
              />
            </Grid.Item>
          </Grid>
          <Grid>
            <Grid.Item>
              <Input
                key={`${address.key}-suburb`}
                label={suburbLabel}
                name='suburb'
                autoComplete='address-level2'
                onChange={handleManualAddressChange}
                onBlur={handleManualAddressBlur}
                size={size}
                value={formData.suburb}
                error={getManualFieldError('suburb')}
                disabled={disabled}
                readOnly={readOnly}
                fullWidth
                {...(testNameSpace.length > 0 && { 'data-dd-action-name': `${testNameSpace} Suburb` })}
                {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-suburb` })}
              />
            </Grid.Item>
          </Grid>
          <Grid>
            <Grid.Item>{renderStateField()}</Grid.Item>
            {renderPostcodeField()}
          </Grid>
          {!showAutoCompeleteErrorTextOnly && !!getErrorMessage({ customValidationString, isError, errorText }) && (
            <div className={styles.linkButtonWrapper}>
              <Text color='Fire' size='XS' {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-manual-error-message` })}>
                {getErrorMessage({ customValidationString, isError, errorText })}
              </Text>
            </div>
          )}
          {searchAddressLabel.length > 0 && (
            <div className={styles.linkButtonWrapper}>
              {renderSearchLabelIcon()}
              <Button.Link
                size={isArcadeDesign ? 'Small' : 'Regular'}
                onClick={handleToggleManualMode}
                {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-auto` })}
                {...(testNameSpace.length > 0 && { 'data-dd-action-name': `${testNameSpace} Auto Complete Search` })}
              >
                {searchAddressLabel}
              </Button.Link>
            </div>
          )}
        </div>
      </>
    )
  }

  const renderSearchLabelIcon = (): ReactNode => {
    if (icon !== undefined && iconPosition !== undefined) {
      return (
        <span className={styles.searchAddressLabelIcon}>
          <Icon name={icon} size='Small' />
        </span>
      )
    }

    return null
  }

  const renderManualAddressInputButton = (): ReactNode => {
    const shouldRender = manualAddressLabel.length > 0 && !showManualAddressLabelInMenu && !readOnly
    if (!shouldRender) {
      return null
    }

    return (
      <div className={styles.linkButtonWrapper}>
        <Button.Link
          onClick={handleManualAddressLabel}
          size='Small'
          disabled={disabled || readOnly}
          {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-manual` })}
        >
          {manualAddressLabel}
        </Button.Link>
      </div>
    )
  }

  const renderSuggestion = (): ReactNode => {
    const shouldRender = suggestions.data.length > 0

    if (!shouldRender) {
      return null
    }

    return (
      <Dropdown.Menu
        key={INPUT_ADDRESS_DROPDOWN_MENU}
        id={INPUT_ADDRESS_DROPDOWN_MENU}
        {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-suggestion` })}
        {...(isShowingFullPageAddress && isArcadeDesign && { borderStyle: DropdownMenuBorderStyle.NO_BORDER })}
        aria-activedescendant={focusedAutocompleteSuggestionId}
      >
        {suggestions.data.map((suggestion: Suggestion, index) => {
          const newId = suggestion.place_id

          return (
            <Dropdown.MenuItem
              key={`${INPUT_ADDRESS_DROPDOWN_MENU}-${newId}`}
              id={newId}
              onClick={() => handleSelect(suggestion)}
              innerRef={(el: HTMLElement) => (dropdownSuggestionRefs.current[index] = el)}
              focus={focusIndex === index}
            >
              <MaskedElement
                actionName='Address Suggestion'
                testNameSpace={testNameSpace.length > 0 ? `${testNameSpace}-suggestion-masked` : 'address-suggestion-masked'}
              >
                {renderOptionString(suggestion)}
              </MaskedElement>
            </Dropdown.MenuItem>
          )
        })}
        {isShowManualAddressLabelInMenu && (
          <Dropdown.MenuItem
            id='manual-address-menu-item-id'
            innerRef={(el: HTMLElement) => (dropdownSuggestionRefs.current[suggestions.data.length] = el)}
            onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
            focus={focusIndex === suggestions.data.length}
          >
            <div className={styles.manualAddressMenuItemLabelWrapper}>
              <span className={styles.manualAddressLabelIcon}>
                <Icon.ArrowRight size='ExtraSmall' />
              </span>
              {manualAddressLabelCaption.length > 0 && <span className={styles.manualAddressLabelCaption}>{manualAddressLabelCaption}</span>}
              <Button.Link
                onClick={handleToggleManualMode}
                disabled={disabled || readOnly}
                {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-manual` })}
              >
                {manualAddressLabel}
              </Button.Link>
            </div>
          </Dropdown.MenuItem>
        )}
      </Dropdown.Menu>
    )
  }

  const renderAutoCompleteQueryInput = (): ReactNode => {
    return (
      <div ref={autoCompleteParentRef}>
        <Input.Text
          {...iconPropsForAutocomplete}
          fullWidth
          name={name}
          label={label}
          size={size}
          key={`${address.key}-autoComplete`}
          readOnly={address.status === InputAddressDecodingStatus.Pending || readOnly}
          ref={autoCompleteRef}
          role='combobox'
          autoComplete='no-autocomplete'
          error={isError}
          errorText={getErrorMessage({ customValidationString, isError, errorText })}
          disabled={disabled}
          onBlur={handleAutocompleteInputBlur}
          onKeyDown={handleKeyDown}
          onChange={handleInput}
          value={address.query}
          autoSelectOnFocus={false}
          {...(testNameSpace.length > 0 && { testNameSpace })}
          isLoading={isGeocoding}
        />
        <div className={styles.suggestionWrapper}>
          <>{renderSuggestion()}</>
        </div>
      </div>
    )
  }

  const autocompleteStyles = clsx(isShowingFullPageAddress ? styles.fullPageWrapper : styles.component)

  const renderAutoComplete = (): ReactNode => {
    const hasSuggestion = suggestions.data.length > 0
    if (hasSuggestion && !showFullPageAddress) {
      componentRef?.current?.scrollIntoView({ behavior: 'smooth' })
    }

    return (
      <Grid>
        <div>
          <div className={autocompleteStyles} ref={componentRef}>
            {renderAutoCompleteQueryInput()}
          </div>
          {renderManualAddressInputButton()}
        </div>
      </Grid>
    )
  }

  const renderComponent = (): ReactNode => {
    if (manualInput) {
      return renderManualInputs()
    }

    return renderAutoComplete()
  }

  const handleGoogleResult = (result: CustomGeocodeResult | null): void => {
    if (result === null) {
      throw new Error('window object is not defined')
    }

    const { formData, formattedAddress } = result

    onGeocodeResult(true)

    setAddress({
      ...address,
      query: formattedAddress,
      status: InputAddressDecodingStatus.Pristine,
      key: Math.random().toString(36),
      valid: true,
    })
    setFormData(formData)
    onAddressChange(formData, false, true)
    setIsGeocoding(false)
  }

  const resetAddressForm = (): void => {
    onGeocodeResult(false)
    setFormData(initialState)
    setAddress({
      ...address,
      status: InputAddressDecodingStatus.Pristine,
    })
    onAddressChange(initialState, false)
    setIsGeocoding(false)
  }

  useEffect(() => {
    scrollDropDownMenu(dropdownSuggestionRefs.current[focusIndex])
  }, [focusIndex])

  useEffect(() => {
    const resolvedKey = (keyProp ?? apiKey ?? '').trim()
    const resolvedVersion = v ?? version ?? 'quarterly'
    const apiKeyOk = resolvedKey !== ''
    if (!apiKeyOk) {
      warn('[Core - InputAddress] API Key has not been provided. Maps API functionality will not work.')
    }
    if (!sdkLoaded && apiKeyOk) {
      setOptions({
        key: resolvedKey,
        v: resolvedVersion,
        libraries,
        language,
        region,
      })

      const setGeocodeStateTrue = (): void => setIsGeocoding(true)

      importLibrary('places').then((): void => {
        init()
        setSdkLoaded(true)

        if (manualInput) {
          resetAddressForm()
        }

        if (!manualInput && typeof initialAddress !== 'undefined') {
          if (Array.isArray(initialAddress) && initialAddress.length === 0) {
            resetAddressForm()
            return
          }

          setAddress((currAddress) => ({
            ...currAddress,
            status: InputAddressDecodingStatus.Pending,
          }))

          if (Array.isArray(initialAddress)) {
            const [primaryAddress, fallbackAddress] = initialAddress

            doGeocodeV2(primaryAddress, country, addressParser, getGeocode, setGeocodeStateTrue)
              .catch(async () => {
                // if the primary address fails
                // perform geocode on the fallbackAddress
                if (typeof fallbackAddress !== 'undefined') {
                  return await doGeocodeV2(fallbackAddress, country, addressParser, getGeocode)
                } else {
                  throw new Error('no addresses left')
                }
              })
              .then(handleGoogleResult)
              .catch(resetAddressForm)
          } else if (typeof initialAddress === 'object' && !Array.isArray(initialAddress)) {
            // initialAddresses is an object..
            doGeocodeV2(initialAddress, country, addressParser, getGeocode, setGeocodeStateTrue).then(handleGoogleResult).catch(resetAddressForm)
          }
        }
      }, resetAddressForm)
    }
  }, [keyProp, apiKey, v, version])

  useEffect(() => {
    if (autoFocus && sdkLoaded) {
      if (manualInput) {
        if (!defaultExpanded) {
          handleManualAddressBlur()
        }
        addressChangeCountRef.current = 0

        const addressLineRef = formData.address1?.length > 0 && address2InfoText.length > 0 ? address2Ref : address1Ref

        addressLineRef.current?.focus()
        address1Ref.current?.scrollIntoView({ behavior: 'smooth' })
      }

      if (!manualInput && toggleCountRef.current > 0) {
        if (addressChangeCountRef.current > 0) {
          const address2 = formData.address2.length > 0 ? ` ${formData.address2}` : ''
          const addressString = `${formData.address1}${address2} ${formData.suburb} ${formData.state} ${formData.postcode}`.trim()
          setAddress({
            ...address,
            query: addressString,
            key: Math.random().toString(36),
          })
        }
        autoCompleteRef.current?.focus()
        autoCompleteRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [manualInput, autoFocus, sdkLoaded])

  return <>{sdkLoaded && renderComponent()}</>
}
